/* ============ Lumeo — global runtime ============ */
(function () {
  'use strict';

  const routes = window.shopUrls || {};
  const strings = window.shopStrings || {};

  /* ---------- helpers ---------- */
  const on = (el, ev, fn, opt) => el && el.addEventListener(ev, fn, opt);
  const money = (cents) => {
    try {
      return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: window.Shopify?.currency?.active || 'BRL' });
    } catch (e) {
      return 'R$ ' + (cents / 100).toFixed(2);
    }
  };

  const CartBus = new EventTarget();
  window.LumeoCart = CartBus;

  async function fetchCart() {
    const res = await fetch(`${routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function addToCart(payload) {
    const res = await fetch(routes.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw data;
    const cart = await fetchCart();
    CartBus.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    CartBus.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
    return data;
  }

  async function changeLine(payload) {
    const res = await fetch(routes.cartChange || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const cart = await res.json();
    CartBus.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    return cart;
  }

  window.LumeoCartAPI = { fetchCart, addToCart, changeLine, money };

  /* ---------- reveal on scroll ---------- */
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initReveals(root = document) {
    const selector = '.reveal:not(.is-visible), [data-reveal-stagger]:not(.is-visible), .mask-reveal:not(.is-visible)';
    const items = root.querySelectorAll(selector);
    if (prefersReduced || !('IntersectionObserver' in window) || !items.length) {
      items.forEach((i) => i.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((i) => io.observe(i));
  }

  /* ---------- ambient cursor light ---------- */
  function initAmbientLight() {
    if (prefersReduced || window.matchMedia('(pointer: coarse)').matches) return;
    if (document.querySelector('.cursor-light')) return;
    const light = document.createElement('div');
    light.className = 'cursor-light';
    document.body.appendChild(light);
    window.addEventListener('pointermove', (e) => {
      light.style.opacity = '1';
      light.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
    }, { passive: true });
    window.addEventListener('pointerleave', () => { light.style.opacity = '0'; });
  }

  /* ---------- scroll progress bar ---------- */
  function initScrollProgress() {
    if (document.querySelector('.scroll-progress')) return;
    const bar = document.createElement('div');
    bar.className = 'scroll-progress';
    document.body.appendChild(bar);
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.width = max > 0 ? (window.scrollY / max) * 100 + '%' : '0%';
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ---------- light parallax ---------- */
  function initParallax(root = document) {
    if (prefersReduced) return;
    const items = Array.from(root.querySelectorAll('[data-parallax]'));
    if (!items.length) return;
    let ticking = false;
    const run = () => {
      items.forEach((el) => {
        const speed = parseFloat(el.dataset.parallax) || 0.15;
        const rect = el.getBoundingClientRect();
        const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) * -speed;
        el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(run); }
    }, { passive: true });
    run();
  }

  /* ---------- generic drawer (dialog-like) ---------- */
  class DrawerController {
    constructor(el) {
      this.el = el;
      this.lastFocus = null;
      el.addEventListener('click', (e) => {
        if (e.target.matches('[data-drawer-close], .drawer__overlay')) this.close();
      });
      on(document, 'keydown', (e) => {
        if (e.key === 'Escape' && this.el.classList.contains('is-open')) this.close();
        if (e.key === 'Tab' && this.el.classList.contains('is-open')) this.trap(e);
      });
    }
    open() {
      this.lastFocus = document.activeElement;
      this.el.classList.add('is-open');
      this.el.removeAttribute('inert');
      document.body.style.overflow = 'hidden';
      const focusable = this.focusables()[0];
      if (focusable) focusable.focus();
      this.el.dispatchEvent(new CustomEvent('drawer:open'));
    }
    close() {
      this.el.classList.remove('is-open');
      this.el.setAttribute('inert', '');
      document.body.style.overflow = '';
      if (this.lastFocus) this.lastFocus.focus();
    }
    focusables() {
      return Array.from(this.el.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'))
        .filter((e) => e.offsetParent !== null);
    }
    trap(e) {
      const f = this.focusables();
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  window.LumeoDrawers = {};
  function initDrawers(root = document) {
    root.querySelectorAll('[data-drawer]').forEach((el) => {
      if (!el.__drawer) el.__drawer = new DrawerController(el);
      window.LumeoDrawers[el.dataset.drawer] = el.__drawer;
    });
    root.querySelectorAll('[data-drawer-open]').forEach((btn) => {
      if (btn.__bound) return;
      btn.__bound = true;
      on(btn, 'click', (e) => {
        e.preventDefault();
        const d = window.LumeoDrawers[btn.dataset.drawerOpen];
        if (d) d.open();
      });
    });
  }

  /* ---------- cart count badges ---------- */
  function updateCartUI(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  }
  on(CartBus, 'cart:updated', (e) => updateCartUI(e.detail));

  /* ---------- product form add-to-cart ---------- */
  function initProductForms(root = document) {
    root.querySelectorAll('form[data-product-form]').forEach((form) => {
      if (form.__bound) return;
      form.__bound = true;
      on(form, 'submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('[type="submit"]');
        const original = btn ? btn.textContent : '';
        if (btn) { btn.disabled = true; btn.textContent = strings.addToCart ? '…' : '…'; }
        try {
          const formData = new FormData(form);
          const payload = { items: [{}] };
          payload.items[0].id = Number(formData.get('id'));
          payload.items[0].quantity = Number(formData.get('quantity') || 1);
          const props = {};
          formData.forEach((v, k) => {
            const m = k.match(/^properties\[(.+)\]$/);
            if (m) props[m[1]] = v;
          });
          if (Object.keys(props).length) payload.items[0].properties = props;
          await addToCart(payload);
          const drawer = window.LumeoDrawers['cart'];
          if (drawer) drawer.open();
        } catch (err) {
          const msg = form.querySelector('[data-form-error]');
          if (msg) msg.textContent = (err && err.description) || strings.unavailable || 'Erro';
        } finally {
          if (btn) { btn.disabled = false; btn.textContent = original; }
        }
      });
    });
  }

  /* ---------- quantity steppers ---------- */
  function initQuantity(root = document) {
    root.querySelectorAll('[data-quantity]').forEach((wrap) => {
      if (wrap.__bound) return;
      wrap.__bound = true;
      const input = wrap.querySelector('input');
      wrap.querySelectorAll('[data-qty-step]').forEach((b) => {
        on(b, 'click', () => {
          const step = Number(b.dataset.qtyStep);
          const min = Number(input.min || 1);
          input.value = Math.max(min, (Number(input.value) || min) + step);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ---------- init pipeline ---------- */
  function initAll(root = document) {
    initReveals(root);
    initDrawers(root);
    initProductForms(root);
    initQuantity(root);
    initParallax(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initAll();
    initAmbientLight();
    initScrollProgress();
    fetchCart().then(updateCartUI).catch(() => {});
  });

  /* ---------- Theme Editor lifecycle ---------- */
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:select', (e) => initReveals(e.target));
})();
